import * as Immutable from 'immutable';
import axios from 'axios';
import qs from 'qs';
import { UserFlags } from '@ordermentum/ui-types';
import { FLAGS_TIMEOUT } from '@ordermentum/ui-constants';
import C from '../../constants';

export const FETCH_FLAGS_REQUEST = 'FETCH_FLAGS_REQUEST';
export const FETCH_FLAGS_SUCCESS = 'FETCH_FLAGS_SUCCESS';
export const FETCH_FLAGS_FAILURE = 'FETCH_FLAGS_FAILURE';

const isDevelopment: boolean = String(process.env.NODE_ENV) === 'development';

const toggleOn = (flags: string[]): UserFlags => {
  let overrides = {};
  try {
    overrides = JSON.parse(localStorage.getItem('flags') ?? '{}');
  } catch (ex) {
    // do nothing
  }

  return Object.assign(
    {},
    ...flags.map(flag => ({ [flag]: overrides[flag] ?? true }))
  );
};

type exceptionT = any;
type actionT = {
  type: string;
  data?: UserFlags;
  error?: exceptionT;
};
type flagsCheckT = Array<string>;
type contextT = {
  [key: string]: any;
};
type dispatchT = (action: actionT) => Promise<any>;

/* --- ACTIONS ------------------------ */
export const fetchFlagsRequest = () => ({
  type: FETCH_FLAGS_REQUEST,
});
export const fetchFlagsSuccess = (data: UserFlags) => ({
  type: FETCH_FLAGS_SUCCESS,
  data,
});
export const fetchFlagsFailure = (error: exceptionT) => ({
  type: FETCH_FLAGS_FAILURE,
  error,
});

const requestCache = new Map();
export const fetchFlagsAsync =
  (flags: flagsCheckT, context: contextT) => async (dispatch: dispatchT) => {
    const qst = qs.stringify({ flags, context }, { arrayFormat: 'brackets' });
    const url = `${C.FLAGS_URL}/v1/flags/check?${qst}`;

    // bail early as we already have this request in flight
    if (requestCache.has(url)) {
      return requestCache.get(url);
    }

    dispatch(fetchFlagsRequest());

    if (isDevelopment) {
      const data = toggleOn(flags);
      requestCache.delete(url);
      return dispatch(fetchFlagsSuccess(data));
    }

    try {
      const promised = axios.get(url, {
        timeout: parseInt(`${FLAGS_TIMEOUT}`, 10),
      });
      requestCache.set(url, promised);
      const res = await promised;
      return dispatch(fetchFlagsSuccess(res.data));
    } catch (ex) {
      return dispatch(fetchFlagsFailure(ex));
    } finally {
      requestCache.delete(url);
    }
  };

type stateT = {
  data: UserFlags;
  loading: boolean;
  error: exceptionT;
};
const initialState = {
  data: {},
  loading: false,
  error: null,
};

export const flagsReducer = (state: stateT = initialState, action: actionT) => {
  const { data = {} } = action;
  switch (action.type) {
    case FETCH_FLAGS_REQUEST: {
      return {
        ...state,
        loading: true,
      };
    }
    case FETCH_FLAGS_SUCCESS: {
      return {
        ...state,
        data: { ...state.data, ...data },
        error: null,
        loading: false,
      };
    }
    case FETCH_FLAGS_FAILURE: {
      return {
        ...state,
        error: action.error,
        loading: false,
      };
    }
    default: {
      return state;
    }
  }
};

export const flagsReducerImmutable = (
  state: any = Immutable.fromJS(initialState),
  action: any
) => {
  switch (action.type) {
    case FETCH_FLAGS_REQUEST: {
      return state.merge({
        loading: true,
      });
    }
    case FETCH_FLAGS_FAILURE: {
      const { error } = action;
      return state.merge({
        loading: false,
        data: {},
        error,
      });
    }
    case FETCH_FLAGS_SUCCESS: {
      const { data } = action;
      return state.mergeDeep({
        loading: false,
        data,
        error: {},
      });
    }
    default:
      return state;
  }
};
